import React, { useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { sessionContext, useGetBusinessID, useGetBusinessIfExists } from '@marketing-milk/frontend'
import { AdCreative } from '@marketing-milk/interfaces'
import { CommentThread } from 'components/comments/CommentThread'
import { useUpdateCreativeCopy } from 'hooks/api/useUpdateCreativeCopy'
import { useCreativesByIds } from 'hooks/creatives/use-creatives-by-ids'
import { ReviewCreativePreviewModal } from 'pages/business/sprint-packets/review/ReviewCreativePreviewModal'
import { ReviewHistory, useHistoryFits } from 'pages/business/sprint-packets/review/ReviewHistory'
import { ReviewHistoryFeed } from 'pages/business/sprint-packets/review/ReviewHistoryFeed'
import { uploadReviewMedia } from 'pages/business/sprint-packets/review/uploadReviewMedia'
import { useReviewKeyNav } from 'pages/business/sprint-packets/review/useReviewKeyNav'
import { usePacketFonts } from 'pages/business/sprint-packets/usePacketFonts'
import { dark } from 'pages/business/sprint-packets/tokens'
import { modalGhostButtonStyle } from 'pages/business/sprint-packets/components/PacketModalShell'
import { creativeCommentUrl } from '../../creativeUrl'

/**
 * The open ad, as `?ad=` on whatever page hosts the editor — so an ad is linkable,
 * and Back closes it. Opening and closing are history entries; walking between
 * ads replaces the entry, so Back leaves the ad rather than retracing the walk.
 * Every other query param the page keeps (a sprint, a tab) rides along untouched.
 */
export function useOpenAd() {
  const history = useHistory()
  const { pathname, search } = useLocation()
  const requested = Number(new URLSearchParams(search).get('ad'))
  const openID = Number.isInteger(requested) && requested > 0 ? requested : undefined

  const withAd = (creativeID?: number) => {
    const params = new URLSearchParams(search)
    if (creativeID === undefined) params.delete('ad')
    else params.set('ad', String(creativeID))
    const query = params.toString()
    return `${pathname}${query ? `?${query}` : ''}`
  }

  return {
    openID,
    open: (creativeID: number) => history.push(withAd(creativeID)),
    walkTo: (creativeID: number) => history.replace(withAd(creativeID)),
    close: () => history.push(withAd()),
  }
}

type Props = {
  /** The host's ads in the order they are on screen, which is the order the arrows walk. */
  creatives: AdCreative[]
  /** How many there are in all under the host's view, loaded or not. */
  total?: number
  /**
   * Load the host's next page, resolving with its whole list after it. Supplied
   * only while there is a next page, so the arrows stop at the true end rather
   * than at the end of what happened to be loaded.
   */
  loadMore?: () => Promise<AdCreative[] | undefined>
}

/**
 * The ad editor, opened over the page that lists the ads rather than on a page of
 * its own. It is the packet review's dialog without the verdict: nobody is being
 * asked to approve anything here, so what's left is the copy, the media and the
 * ad's history.
 */
export function CreativeEditorModal({ creatives, total, loadMore }: Props) {
  usePacketFonts()
  const businessID = Number(useGetBusinessID())
  const { openID, walkTo, close } = useOpenAd()
  const updateCopy = useUpdateCreativeCopy(businessID)

  // A comment link names the comment to read, so the history arrives open on it.
  // The feed ignores an id that isn't on the ad it shows, so walking off the
  // linked ad simply stops matching. Read from the URL every render, not once:
  // this stays mounted for the whole visit, and a second link can land on it.
  const { search } = useLocation()
  const commentParam = Number(new URLSearchParams(search).get('commentId'))
  const targetCommentID = Number.isInteger(commentParam) && commentParam > 0 ? commentParam : null

  // Held here rather than in the dialog, which is remounted per ad, so history
  // stays open across prev/next.
  const [historyOpen, setHistoryOpen] = useState(false)
  const historyFits = useHistoryFits()
  const [commenting, setCommenting] = useState(false)
  useEffect(() => {
    if (targetCommentID === null) return
    setHistoryOpen(true)
    // Without room for the history column, the drawer is the only place the
    // linked comment can be shown.
    setCommenting(!historyFits)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCommentID])
  // The next ad opened starts without the last one's drawer.
  useEffect(() => {
    if (openID === undefined) setCommenting(false)
  }, [openID])
  const business = useGetBusinessIfExists()
  const { user } = useContext(sessionContext)

  // An ad reached by paging past the loaded list is known before the host's list
  // re-renders with it; holding it bridges that gap without a fetch of its own.
  const [landed, setLanded] = useState<AdCreative>()

  const index = openID === undefined ? -1 : creatives.findIndex(({ id }) => id === openID)
  const listed = index >= 0 ? creatives[index] : landed?.id === openID ? landed : undefined

  // A link can name an ad the host hasn't loaded — a later page, another sprint,
  // or one the host's view filters out. It opens all the same, just outside the
  // host's order.
  const { data: linked } = useCreativesByIds(openID !== undefined && !listed ? [openID] : [])
  const creative = listed ?? linked?.[0]

  // The walk resolves after an await, and the reviewer may have closed or moved on
  // by then; it only lands if they are still on the ad it started from.
  const current = useRef(openID)
  current.current = openID
  // The ref guards against a second press before the re-render lands (a held
  // arrow key repeats faster than that); the state is what locks the fields.
  const loadingRef = useRef(false)
  const [loading, setLoading] = useState(false)

  const goToPrev = () => {
    const prev = creatives[index - 1]
    if (index > 0 && prev) walkTo(prev.id)
  }

  const goToNext = async () => {
    if (index < 0) return
    const next = creatives[index + 1]
    if (next) return walkTo(next.id)
    if (!loadMore || loadingRef.current) return
    const from = openID
    loadingRef.current = true
    setLoading(true)
    try {
      const after = (await loadMore())?.[index + 1]
      if (after && current.current === from) {
        setLanded(after)
        walkTo(after.id)
      }
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }

  useReviewKeyNav({ active: !!creative, onPrev: goToPrev, onNext: goToNext })

  if (!creative) return null

  // Escape and the close control both land here, and the dialog listens for
  // Escape on the whole document — so a keypress while typing a comment would
  // otherwise shut the ad out from under it.
  const onClose = () => {
    if (commenting && document.activeElement?.closest('.MuiDrawer-paper')) setCommenting(false)
    else close()
  }

  return (
    <>
      <ReviewCreativePreviewModal
        key={creative.id}
        creative={creative}
        navigationPending={loading}
        position={index >= 0 ? { current: index + 1, total: total ?? creatives.length } : undefined}
        hasPrev={index > 0}
        hasNext={index >= 0 && (index < creatives.length - 1 || !!loadMore)}
        onPrev={goToPrev}
        onNext={goToNext}
        onSaveCopy={patch => updateCopy.mutateAsync({ creativeID: creative.id, patch })}
        onUploadMedia={file => uploadReviewMedia(businessID, file)}
        onOpenComments={() => setCommenting(true)}
        historySlot={
          <ReviewHistory open={historyOpen} onToggle={() => setHistoryOpen(value => !value)}>
            {historyOpen && (
              <>
                <button
                  type="button"
                  onClick={() => setCommenting(true)}
                  style={{ ...modalGhostButtonStyle(dark), width: '100%', marginBottom: 12 }}
                >
                  <i
                    className="fa fa-comment mr-2"
                    style={{ color: 'inherit' }}
                    aria-hidden="true"
                  />
                  Add a comment
                </button>
                <ReviewHistoryFeed
                  businessID={businessID}
                  creativeID={creative.id}
                  targetCommentID={targetCommentID}
                />
              </>
            )}
          </ReviewHistory>
        }
        onClose={onClose}
      />
      {/* Outside the dialog on purpose: the drawer is portaled, but React still
        bubbles its clicks through the tree it was rendered in, and the dialog's
        backdrop closes on a click. */}
      <CommentThread
        open={commenting}
        onClose={() => setCommenting(false)}
        businessID={businessID}
        anchor={{ type: 'creative', id: String(creative.id), label: creative.name }}
        targetCommentId={targetCommentID}
        notificationContext={{
          header: `${user?.firstName ?? ''} ${
            user?.lastName ?? ''
          } mentioned you on this creative: "${creative.name}" for business: "${
            business?.businessName ?? ''
          }"`,
          pageUrlBase: `${window.location.origin}/builder/${businessID}/creatives`,
          pageUrlForAnchor: (anchor, commentId) =>
            creativeCommentUrl(businessID, anchor.id, commentId),
          slackChannelId: business?.slackChannelId,
        }}
      />
    </>
  )
}
